import React from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Button, VStack, Text, Alert, AlertIcon,
  Flex, Box, Badge, Divider, Avatar, useColorModeValue,
  HStack, Stack, Heading, Icon
} from '@chakra-ui/react';
import { CheckIcon, InfoIcon, ChatIcon, TimeIcon, WarningIcon, QuestionIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import { FiUser, FiMessageCircle, FiMail, FiPhone, FiCalendar, FiFileText, FiHelpCircle } from 'react-icons/fi';

/**
 * Detail Item Component for Modal
 */
const DetailItem = ({ label, value }) => (
  <Box mb={2}>
    <Text fontWeight="bold" fontSize="sm" color="gray.500">
      {label}
    </Text>
    <Text>{value}</Text>
  </Box>
);

/**
 * Status Badge Component
 */
const StatusBadge = ({ isCompleted, isInProgress, isPendingConfirmation }) => {
  let colorScheme, status, icon;

  if (isCompleted) {
    colorScheme = 'green';
    status = 'Concluído';
    icon = CheckIcon;
  } else if (isPendingConfirmation) {
    colorScheme = 'orange';
    status = 'Aguardando Confirmação';
    icon = QuestionIcon;
  } else if (isInProgress) {
    colorScheme = 'yellow';
    status = 'Em Andamento';
    icon = TimeIcon;
  } else {
    colorScheme = 'red';
    status = 'Aguardando';
    icon = WarningIcon;
  }

  return (
    <Badge colorScheme={colorScheme} px={2} py={1} borderRadius="full">
      <Flex align="center">
        <Icon as={icon} mr={1} boxSize="10px" />
        <Text>{status}</Text>
      </Flex>
    </Badge>
  );
};

// Message bubble component
const MessageBubble = ({ message, userName }) => {
  const isUser = message.senderName === userName;
  const bubbleBg = useColorModeValue(
    isUser ? 'cyan.50' : 'purple.50',
    isUser ? 'cyan.900' : 'purple.900'
  );
  const borderColor = useColorModeValue(
    isUser ? 'cyan.200' : 'purple.200',
    isUser ? 'cyan.700' : 'purple.700'
  );
  const headerColor = useColorModeValue(
    isUser ? 'cyan.700' : 'purple.700',
    isUser ? 'cyan.300' : 'purple.300'
  );
  
  return (
    <Flex 
      direction="column"
      mb={3}
      alignSelf={isUser ? "flex-end" : "flex-start"}
      maxW="80%"
    >
      <Flex align="center" mb={1} gap={2}>
        <Avatar 
          size="xs" 
          name={message.senderName || 'Unknown'} 
          bg={isUser ? "cyan.500" : "purple.500"}
        />
        <Text fontWeight="bold" fontSize="sm" color={headerColor}>
          {message.senderName || 'Unknown'}
        </Text>
      </Flex>
      
      <Box
        bg={bubbleBg}
        p={3}
        borderRadius="lg"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Text>{message.content || 'No content available'}</Text>
        <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
          {message.timestamp ? new Date(message.timestamp).toLocaleString() : 'No timestamp available'}
        </Text>
      </Box>
    </Flex>
  );
};

/**
 * UserHelpDetailsModal Component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.helpData - Data for the help request
 * @param {Function} props.onActionTaken - Function to handle action taken
 * @param {Function} props.onOpenChat - Function to open the chat
 */
const UserHelpDetailsModal = ({ 
  isOpen, 
  onClose, 
  helpData, 
  onActionTaken, 
  onOpenChat 
}) => {
  // Colors
  const modalHeaderBg = useColorModeValue('cyan.50', 'cyan.900');
  const modalHeaderColor = useColorModeValue('cyan.700', 'cyan.100');
  const mainBoxBg = useColorModeValue('gray.50', 'gray.700');
  const reasonBoxBg = useColorModeValue('cyan.50', 'cyan.900');
  const conversationBoxBg = useColorModeValue('purple.50', 'purple.900');
  const userBoxBg = useColorModeValue('teal.50', 'teal.900');
  const filterBorderColor = useColorModeValue('gray.200', 'gray.600');
  const trackBg = useColorModeValue('gray.100', 'gray.800');
  const thumbBg = useColorModeValue('cyan.200', 'cyan.700');
  
  // Early return with empty modal if no data
  if (!helpData) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>No data available</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>No help request data available</Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }
  
  const {
    _id,
    userId,
    reason,
    additionalNotes,
    isConversation,
    conversation,
    serviceDetails,
    actionTaken,
    inProgress,
    pendingConfirmation,
    createdAt
  } = helpData;
  
  const userName = userId?.name || 'Usuário Desconhecido';
  const hasMessages = conversation && conversation.messages && conversation.messages.length > 0;
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="md">
        <ModalHeader 
          bg={modalHeaderBg}
          color={modalHeaderColor}
          borderTopRadius="md"
          py={4}
        >
          <HStack>
            <Text>Detalhes da Solicitação de Ajuda</Text>
            <StatusBadge
              isCompleted={actionTaken}
              isInProgress={inProgress}
              isPendingConfirmation={pendingConfirmation}
            />
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={4}>
          <VStack align="stretch" spacing={4}>
            {/* Main Information */}
            <Box 
              p={4} 
              bg={mainBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="cyan.500">
                Informações Principais
              </Heading>
              <Stack spacing={3}>
                <Flex flexWrap="wrap" justify="space-between">
                  <DetailItem label="ID" value={_id || 'N/A'} />
                  <DetailItem 
                    label="Data de Criação" 
                    value={
                      createdAt
                        ? new Date(createdAt).toLocaleString()
                        : 'N/A'
                    } 
                  />
                </Flex>
              </Stack>
            </Box>
            
            {/* Reason Box */}
            <Box 
              p={4} 
              bg={reasonBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="cyan.500">
                Razão da Solicitação
              </Heading>
              <DetailItem label="Motivo" value={reason || 'N/A'} />
              
              {additionalNotes && (
                <DetailItem label="Notas Adicionais" value={additionalNotes} />
              )}
            </Box>
            
            {/* Service Details */}
            {!isConversation && serviceDetails && (
              <Box 
                p={4} 
                bg={reasonBoxBg} 
                borderRadius="md"
                borderWidth="1px"
                borderColor={filterBorderColor}
              >
                <Heading as="h3" size="sm" mb={3} color="cyan.500">
                  Detalhes do Serviço
                </Heading>
                <Stack spacing={3}>
                  <DetailItem 
                    label="Descrição" 
                    value={serviceDetails?.description || 'N/A'} 
                  />
                  <Flex flexWrap="wrap" justify="space-between">
                    <DetailItem 
                      label="Status" 
                      value={serviceDetails?.status || 'N/A'} 
                    />
                    <DetailItem 
                      label="Valor" 
                      value={serviceDetails?.value || 'N/A'} 
                    />
                  </Flex>
                  <Flex flexWrap="wrap" justify="space-between">
                    <DetailItem 
                      label="Data de Início" 
                      value={serviceDetails?.startDate 
                        ? new Date(serviceDetails.startDate).toLocaleDateString()
                        : 'N/A'
                      } 
                    />
                    <DetailItem 
                      label="Data de Término" 
                      value={serviceDetails?.endDate 
                        ? new Date(serviceDetails.endDate).toLocaleDateString()
                        : 'N/A'
                      } 
                    />
                  </Flex>
                </Stack>
              </Box>
            )}
            
            {/* User Information */}
            <Box 
              p={4} 
              bg={userBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="teal.500">
                Informações do Usuário
              </Heading>
              <Stack spacing={3}>
                <DetailItem 
                  label="Nome" 
                  value={userId?.name || 'N/A'} 
                />
                <Flex flexWrap="wrap" justify="space-between">
                  <DetailItem 
                    label="Email" 
                    value={userId?.email || 'N/A'} 
                  />
                  <DetailItem 
                    label="Telefone" 
                    value={userId?.phone || 'N/A'} 
                  />
                </Flex>
              </Stack>
            </Box>
            
            {/* Conversation Messages (if available) */}
            {isConversation && hasMessages && (
              <Box 
                p={4} 
                bg={conversationBoxBg} 
                borderRadius="md"
                borderWidth="1px"
                borderColor={filterBorderColor}
              >
                <Heading as="h3" size="sm" mb={3} color="purple.500">
                  Conversa
                </Heading>
                
                <Box
                  maxH="400px"
                  overflowY="auto"
                  display="flex"
                  flexDirection="column"
                  p={3}
                  borderWidth="1px"
                  borderColor={filterBorderColor}
                  borderRadius="md"
                  bg={mainBoxBg}
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: trackBg,
                      borderRadius: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: thumbBg,
                      borderRadius: '8px',
                    },
                  }}
                >
                  {conversation.messages.map((message, index) => (
                    <MessageBubble 
                      key={index} 
                      message={message} 
                      userName={userName} 
                    />
                  ))}
                </Box>
                
                {conversation?.participants && conversation.participants.length > 0 && (
                  <Box mt={3}>
                    <Text fontWeight="bold" fontSize="sm" color="gray.500" mb={2}>
                      Participantes:
                    </Text>
                    <Flex flexWrap="wrap" gap={2}>
                      {conversation.participants.map((participant, index) => (
                        <Flex key={index} align="center">
                          <Avatar 
                            size="xs" 
                            name={participant?.name || 'Unknown'} 
                            mr={1} 
                          />
                          <Text fontSize="sm">
                            {participant?.name || 'Desconhecido'} 
                          </Text>
                        </Flex>
                      ))}
                    </Flex>
                  </Box>
                )}
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px" borderColor={filterBorderColor}>
          <HStack spacing={3}>
            {inProgress && !actionTaken && !pendingConfirmation && (
              <Button
                leftIcon={<CheckIcon />}
                colorScheme="green"
                onClick={() => onActionTaken(_id)}
              >
                Finalizar
              </Button>
            )}
            {pendingConfirmation && !actionTaken && (
              <Badge colorScheme="orange" px={3} py={2} borderRadius="md">
                <Flex align="center">
                  <Icon as={QuestionIcon} mr={2} />
                  <Text>Aguardando resposta do usuário</Text>
                </Flex>
              </Badge>
            )}
            {userId && userId._id && (
              <Button
                leftIcon={<FiMessageCircle />}
                colorScheme="teal"
                variant="outline"
                onClick={() => onOpenChat(userId._id)}
              >
                Chat com Usuário
              </Button>
            )}
            <Button onClick={onClose} variant="ghost">
              Fechar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UserHelpDetailsModal;
